import React, { useState, useEffect, useCallback } from 'react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, Legend
} from 'recharts';
import { Calendar, RefreshCw, DollarSign, Users, FlaskConical, Clock, BarChart3 } from 'lucide-react';
import { API_URL } from '../../config/api';

const COLORS = ['#062c61', '#00BBBF', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];
const toYMD = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const STATUS_LABELS = {
    solicitado: 'Solicitado',
    pendiente_muestra: 'Pendiente Muestra',
    muestra_tomada: 'Muestra Tomada',
    en_laboratorio: 'En Laboratorio',
    finalizado: 'Finalizado',
    cancelado: 'Cancelado'
};

/* "?"? Custom Tooltip "?"? */
const CustomTooltip = ({ active, payload, label, formatter }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="custom-tooltip">
            <div className="tooltip-label">{typeof label === 'string' && label.includes('-') ? new Date(label).toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric', month: 'short' }) : label}</div>
            {payload.map((entry, i) => (
                <div key={i} className="tooltip-value" style={{ color: entry.color || '#fff' }}>
                    {entry.name}: {formatter ? formatter(entry.value) : entry.value}
                </div>
            ))}
        </div>
    );
};

/* "?"? Skeleton Loading "?"? */
const SkeletonDashboard = () => (
    <>
        <div className="skeleton-grid">
            {[1, 2, 3, 4].map(i => (
                <div className="skeleton-card" key={i}>
                    <div className="skeleton-icon" />
                    <div className="skeleton-text">
                        <div className="skeleton-line short" />
                        <div className="skeleton-line tall" />
                    </div>
                </div>
            ))}
        </div>
        <div className="charts-row">
            <div className="skeleton-chart"><div className="skeleton-line short" /><div className="skeleton-chart-inner" /></div>
            <div className="skeleton-chart"><div className="skeleton-line short" /><div className="skeleton-chart-inner" /></div>
        </div>
    </>
);

/* "?"? Date Presets "?"? */
const getPresetRange = (preset) => {
    const now = new Date();
    const today = toYMD(now);
    switch (preset) {
        case 'today': return { start: today, end: today };
        case 'week': {
            const startOfWeek = new Date(now);
            startOfWeek.setDate(now.getDate() - now.getDay());
            return { start: toYMD(startOfWeek), end: today };
        }
        case 'month': {
            return { start: toYMD(new Date(now.getFullYear(), now.getMonth(), 1)), end: today };
        }
        case 'quarter': {
            const qMonth = Math.floor(now.getMonth() / 3) * 3;
            return { start: toYMD(new Date(now.getFullYear(), qMonth, 1)), end: today };
        }
        case 'year': {
            return { start: toYMD(new Date(now.getFullYear(), 0, 1)), end: today };
        }
        default: return { start: today, end: today };
    }
};

/* "?"? Main Component "?"? */
const ReportsDashboard = ({ onNavigate }) => {
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [activePreset, setActivePreset] = useState('month');
    const [dateRange, setDateRange] = useState(() => getPresetRange('month'));
    const [data, setData] = useState({
        kpis: { total_patients: 0, active_studies: 0, pending_samples: 0, monthly_income: 0 },
        charts: { patients_growth: [], studies_status: [] }
    });

    const fetchData = useCallback(async (isRefresh = false) => {
        if (isRefresh) setRefreshing(true); else setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(
                `${API_URL}/reports/dashboard?start_date=${dateRange.start}&end_date=${dateRange.end}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!res.ok) throw new Error('Error al cargar datos del dashboard');
            setData(await res.json());
        } catch (err) {
            console.error(err);
            setError(err.message);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [dateRange]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handlePreset = (preset) => {
        setActivePreset(preset);
        setDateRange(getPresetRange(preset));
    };

    const handleDateChange = (e) => {
        setActivePreset('');
        setDateRange(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const fmt = (value) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(value || 0);

    const presets = [
        { key: 'today', label: 'Hoy' },
        { key: 'week', label: 'Semana' },
        { key: 'month', label: 'Mes' },
        { key: 'quarter', label: 'Trimestre' },
        { key: 'year', label: 'Año' },
    ];

    const kpis = [
        { key: 'monthly_income', title: 'Ingresos', icon: DollarSign, iconClass: 'income', value: fmt(data.kpis.monthly_income), nav: 'financial' },
        { key: 'total_patients', title: 'Pacientes Nuevos', icon: Users, iconClass: 'patients', value: data.kpis.total_patients },
        { key: 'active_studies', title: 'Estudios Activos', icon: FlaskConical, iconClass: 'studies', value: data.kpis.active_studies, nav: 'studies' },
        { key: 'pending_samples', title: 'Muestras Pendientes', icon: Clock, iconClass: 'pending', value: data.kpis.pending_samples, valueClass: 'warning' },
    ];

    /* Process chart data: translate status names */
    const studiesStatusData = (data.charts.studies_status || []).map(item => ({
        ...item,
        label: STATUS_LABELS[item.status] || item.status
    }));

    return (
        <div className="reports-dashboard">
            {/* Header */}
            <div className="report-header-actions">
                <div className="report-title-wrapper">
                    <div className="report-title">
                        <BarChart3 size={28} />
                        <h2>Dashboard General</h2>
                    </div>
                    <p>Resumen de indicadores clave del periodo seleccionado</p>
                </div>
                <div className="report-controls">
                    <div className="date-range-picker">
                        <Calendar size={16} />
                        <input type="date" name="start" value={dateRange.start} onChange={handleDateChange} className="date-input" />
                        <span>a</span>
                        <input type="date" name="end" value={dateRange.end} onChange={handleDateChange} className="date-input" />
                    </div>
                    <button className={`btn-icon ${refreshing ? 'spinning' : ''}`} onClick={() => fetchData(true)} title="Actualizar">
                        <RefreshCw size={18} />
                    </button>
                </div>
            </div>

            {/* Presets */}
            <div className="date-presets" style={{ marginBottom: '1.5rem' }}>
                {presets.map(p => (
                    <button
                        key={p.key}
                        className={`preset-chip ${activePreset === p.key ? 'active' : ''}`}
                        onClick={() => handlePreset(p.key)}
                    >
                        {p.label}
                    </button>
                ))}
            </div>

            {error && <div className="error-message">⚠️ {error}</div>}

            {/* Content */}
            {loading ? <SkeletonDashboard /> : (
                <>
                    {/* KPIs */}
                    <div className="kpi-grid">
                        {kpis.map(({ key, title, icon: Icon, iconClass, value, valueClass, nav }) => (
                            <div
                                key={key}
                                className={`kpi-card ${nav ? 'clickable' : ''}`}
                                onClick={() => nav && onNavigate?.(nav)}
                                title={nav ? `Ver reporte de ${title}` : undefined}
                            >
                                <div className={`kpi-icon-wrapper ${iconClass}`}>
                                    <Icon size={24} />
                                </div>
                                <div>
                                    <span className="kpi-title">{title}</span>
                                    <span className={`kpi-value ${valueClass || ''}`}>{value}</span>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Charts */}
                    <div className="charts-row">
                        <div className="chart-container">
                            <h3 className="chart-title">📈 Crecimiento de Pacientes</h3>
                            {data.charts.patients_growth?.length > 0 ? (
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={data.charts.patients_growth}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                        <XAxis
                                            dataKey="day"
                                            tickFormatter={(s) => new Date(s).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}
                                            tick={{ fill: '#94a3b8', fontSize: 12 }}
                                            axisLine={{ stroke: '#e2e8f0' }}
                                            tickLine={false}
                                        />
                                        <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={false} tickLine={false} />
                                        <Tooltip content={<CustomTooltip />} />
                                        <Bar dataKey="count" name="Pacientes" fill="#062c61" radius={[6, 6, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="no-data">No hay datos de pacientes en este rango</div>
                            )}
                        </div>

                        <div className="chart-container">
                            <h3 className="chart-title">📊 Estado de Estudios</h3>
                            {studiesStatusData.length > 0 ? (
                                <ResponsiveContainer width="100%" height={300}>
                                    <PieChart>
                                        <Pie
                                            data={studiesStatusData}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={60}
                                            outerRadius={100}
                                            paddingAngle={3}
                                            dataKey="count"
                                            nameKey="label"
                                            label={({ label, percent }) => `${label} ${(percent * 100).toFixed(0)}%`}
                                            labelLine={false}
                                        >
                                            {studiesStatusData.map((_, i) => (
                                                <Cell key={i} fill={COLORS[i % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                        <Legend formatter={(value) => <span style={{ color: '#334155', fontSize: '0.82rem' }}>{value}</span>} />
                                    </PieChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="no-data">No hay datos de estudios en este rango</div>
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default ReportsDashboard;
